import React, { useEffect, useRef, useState } from 'react';
import { formatAlertText, getAlertDetailMessage } from './alertUtils';

export default function ComponentAlertRow({ alert, severity = 'warning' }) {
  const [expanded, setExpanded] = useState(false);
  const labelRef = useRef(null);
  const label = formatAlertText(alert);
  const detailMessage = getAlertDetailMessage(alert);
  const hasDetail = Boolean(detailMessage);

  useEffect(() => {
    if (!hasDetail || expanded || !labelRef.current || !window.bootstrap?.Tooltip) return undefined;

    const tooltip = new window.bootstrap.Tooltip(labelRef.current, {
      title: detailMessage,
      trigger: 'hover focus',
    });

    return () => tooltip.dispose();
  }, [detailMessage, expanded, hasDetail]);

  const iconClass = {
    ok: 'bi-check-circle-fill text-success',
    warning: 'bi-exclamation-triangle-fill text-warning',
    fault: 'bi-x-octagon-fill text-danger',
  }[severity] || 'bi-exclamation-triangle-fill text-warning';

  const bgColor = {
    ok: '#e6f4ea',
    warning: '#fffbe6',
    fault: '#f8d7da',
  }[severity];

  function handleClick() {
    if (hasDetail) {
      setExpanded((value) => !value);
    }
  }

  function handleKeyDown(event) {
    if (!hasDetail) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setExpanded((value) => !value);
    }
  }

  return (
    <div className="mb-2">
      <div
        className="d-flex align-items-center justify-content-between px-3 py-2"
        style={{
          backgroundColor: bgColor,
          borderRadius: '8px',
          fontWeight: 600,
          cursor: hasDetail ? 'pointer' : undefined,
        }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={hasDetail ? 'button' : undefined}
        tabIndex={hasDetail ? 0 : undefined}
        aria-expanded={hasDetail ? expanded : undefined}
      >
        <span className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
          <i className={`bi ${iconClass} fs-5 flex-shrink-0`} />
          <span ref={labelRef} className="text-break">
            {label}
          </span>
        </span>
        {hasDetail && (
          <i className={`bi bi-chevron-${expanded ? 'down' : 'right'} fs-5 text-secondary flex-shrink-0 ms-2`} />
        )}
      </div>
      {expanded && hasDetail && (
        <div
          className="px-3 pt-2 pb-1 text-muted small"
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {detailMessage}
        </div>
      )}
    </div>
  );
}
